import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {ApplicationComposerToolbarEntryPointModel} from '../../../../../models/api/applicationEntryPointModel';
import {ApplicationModel} from '../../../../../models/api/applicationModel';
import {popoutApplicationAction} from '../../../../../store/panels/applications/applicationPanelActions';
import {
  ApplicationPanelLocationsEnum,
  ApplicationsPanelState,
} from '../../../../../store/panels/applications/applicationPanelState';
import {removePanelAction} from '../../../../../store/panels/panelsActions';
import {
  ApplicationsPanelId,
  ComposerPanelId,
  ViewerPanelId,
} from '../../../../../store/panels/panelStateBase';
import {DropdownItemAvatar} from '../../../../common/dropdowns/dropdownItemAvatar';
import {DropdownItemIcon} from '../../../../common/dropdowns/dropdownItemIcon';
import {ApplicationComposerEntryPointPanel} from './applicationComposerEntryPointPanel';

/*
 * Props.
 */

interface ApplicationComposerEntryPointOverflowButtonProps {
  entryPoints: ReadonlyArray<ApplicationComposerToolbarEntryPointModel>;
  applications: ReadonlyArray<ApplicationModel>;
  panel?: ApplicationsPanelState;
  panelId: ComposerPanelId | ViewerPanelId;
}

/*
 * Style.
 */

const StyledButtonImg = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 9000px;
`;

const StyledDropdownItemAvatar = styled(DropdownItemAvatar)`
  display: flex;
`;

/*
 * Component.
 */

export const ApplicationComposerEntryPointOverflowButton: FC<
  ApplicationComposerEntryPointOverflowButtonProps
> = (props) => {
  const {panelId, entryPoints, applications, panel} = props;
  const buttonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const openComposerPanel = (entryPoint: ApplicationComposerToolbarEntryPointModel) => {
    dispatch(
      popoutApplicationAction(entryPoint, {
        location: ApplicationPanelLocationsEnum.POPOVER,
        parentPanelId: panelId,
      }),
    );
  };

  const onRequestPanelClose = (id: ApplicationsPanelId) => {
    dispatch(removePanelAction(id));
  };

  if (entryPoints.length === 0) {
    return null;
  }

  return (
    <>
      <PopoverCoordinator
        renderButton={(hasHover) => (
          <IconButton
            ref={buttonRef}
            iconName="moreVertical"
            hasHover={hasHover || Boolean(panel)}
            label={null}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
        )}
        renderModal={(popoverProps) => (
          <SimpleDropdown anchor={popoverProps.anchor} onRequestClose={popoverProps.onRequestClose}>
            {entryPoints.map((entryPoint) =>
              renderDropdownItem(entryPoint, applications, theme.greys.shade80, () =>
                openComposerPanel(entryPoint),
              ),
            )}
          </SimpleDropdown>
        )}
      />
      {panel && (
        <ApplicationComposerEntryPointPanel
          panelId={panel.id}
          frame={panel.frame}
          anchor={buttonRef}
          onRequestClose={() => onRequestPanelClose(panel.id)}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function renderDropdownItem(
  entryPoint: ApplicationComposerToolbarEntryPointModel,
  applications: ReadonlyArray<ApplicationModel>,
  iconColor: string,
  onClick: Handler,
) {
  const application = applications.find((app) => app.id === entryPoint.applicationId);

  if (!application) {
    return null;
  }
  return (
    <DropdownItem key={`icon-dropdown-item-${entryPoint.frameId}`} onSelect={onClick}>
      {!application.iconUrl && <DropdownItemIcon iconName="integration" label={null} color={iconColor} />}
      {application.iconUrl && (
        <StyledDropdownItemAvatar>
          <StyledButtonImg src={application.iconUrl} />
        </StyledDropdownItemAvatar>
      )}
      <DropdownItemContent>{application.name}</DropdownItemContent>
    </DropdownItem>
  );
}
